// Super Master > Vincular veículos (pedido 2026-10-03): escolhe empresa e
// cliente, cola a lista de placas e o sistema acha cada veículo e vincula
// ao cliente sozinho. Só SOMA vínculo — nunca tira o veículo de ninguém
// nem mexe na ficha. Primeiro "Buscar placas" (mostra o que achou), depois
// "Vincular". Busca pela placa do veículo e, sem resultado, pela placa
// escrita no nome; Mercosul e antiga casam entre si (ver
// vehicle-link.service.ts no backend).
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, CheckCircle2, ChevronsUpDown, Link2, Loader2, Search } from "lucide-react";
import { MasterShell } from "@/components/master-shell";
import { MasterSmsTabs } from "@/components/master-sms-tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useCompanies, useMigrationClients } from "@/hooks/queries/platform";
import { platformPost, PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/master/vehicle-link")({
  component: () => (
    <MasterShell>
      <MasterSmsTabs />
      <VehicleLinkPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Vincular veículos — Super Master — GPS Drive" }] }),
});

interface LinkResult {
  plate: string;
  status: "link" | "linked" | "already" | "not_found" | "ambiguous" | "other_company" | "error";
  deviceId?: number;
  deviceName?: string;
  imei?: string;
  candidates?: string[];
  error?: string;
}

const STATUS_LABEL: Record<LinkResult["status"], { label: string; cls: string }> = {
  link: { label: "Vai vincular", cls: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200" },
  linked: { label: "Vinculados", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" },
  already: { label: "Já era do cliente", cls: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200" },
  not_found: { label: "Não encontrada", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200" },
  ambiguous: { label: "Mais de um veículo", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" },
  other_company: { label: "Outra empresa", cls: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200" },
  error: { label: "Erro", cls: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200" },
};

const BATCH = 300;

// Aceita uma placa por linha, ou separadas por vírgula/ponto e vírgula/
// tab (colado do Excel). Repetidas contam uma vez.
function parsePlates(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[\n,;\t]+/)) {
    const p = raw.trim().toUpperCase();
    const key = p.replace(/[^A-Z0-9]/g, "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

function VehicleLinkPage() {
  const { data: companies } = useCompanies();
  const [companyId, setCompanyId] = useState<string>("");
  const { data: clients, isLoading: clientsLoading } = useMigrationClients(companyId || undefined);
  const [clientId, setClientId] = useState<number | undefined>();
  const [clientOpen, setClientOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState<null | "analyze" | "apply">(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<LinkResult[] | null>(null);
  const [applied, setApplied] = useState(false);
  const [filter, setFilter] = useState<LinkResult["status"] | "">("");

  const plates = useMemo(() => parsePlates(text), [text]);
  const client = clients?.find((c) => c.traccarUserId === clientId);

  const reset = () => {
    setResults(null);
    setApplied(false);
    setFilter("");
  };

  const run = async (dryRun: boolean) => {
    if (!companyId || !clientId) return;
    // Vincular: só as placas que a busca disse que vão vincular.
    // Mesmo veículo listado por placa E por IMEI vincula uma vez só.
    const seenDevices = new Set<number>();
    const toSend = dryRun
      ? plates
      : (results ?? [])
          .filter((r) => r.status === "link" && r.deviceId != null && !seenDevices.has(r.deviceId) && seenDevices.add(r.deviceId))
          .map((r) => r.plate);
    if (toSend.length === 0) return;
    setBusy(dryRun ? "analyze" : "apply");
    setProgress({ done: 0, total: toSend.length });
    const out: LinkResult[] = [];
    try {
      for (let i = 0; i < toSend.length; i += BATCH) {
        const res = await platformPost<LinkResult[]>(`/api/v1/platform-migration/companies/${companyId}/vehicle-link`, {
          dryRun,
          traccarUserId: clientId,
          plates: toSend.slice(i, i + BATCH),
        });
        out.push(...res);
        setProgress({ done: Math.min(i + BATCH, toSend.length), total: toSend.length });
      }
      if (dryRun) {
        setResults(out);
        setApplied(false);
        setFilter("");
      } else {
        const ok = out.filter((r) => r.status === "linked").length;
        const errors = out.filter((r) => r.status === "error").length;
        toast.success(`${ok} veículo(s) vinculados a ${client?.name ?? "o cliente"}`, errors ? { description: `${errors} com erro — veja a lista.` } : undefined);
        setApplied(true);
      }
    } catch (err) {
      toast.error(dryRun ? "Não foi possível buscar os veículos" : "Parou no meio da vinculação", {
        description: err instanceof PlatformApiError || err instanceof Error ? err.message : undefined,
      });
    } finally {
      if (!dryRun && out.length) {
        const byPlate = new Map(out.map((r) => [r.plate, r]));
        setResults((prev) => (prev ?? []).map((r) => byPlate.get(r.plate) ?? r));
      }
      setBusy(null);
    }
  };

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of results ?? []) c[r.status] = (c[r.status] ?? 0) + 1;
    return c;
  }, [results]);
  const visible = (results ?? []).filter((r) => !filter || r.status === filter);
  const statuses = (Object.keys(STATUS_LABEL) as LinkResult["status"][]).filter((s) => counts[s]);

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Link2 className="h-6 w-6 text-sky-500" /> Vincular veículos
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Escolha a empresa e o cliente, cole a lista de placas ou IMEIs (um embaixo do outro) e o sistema acha cada veículo e vincula ao cliente.
          Só adiciona — nenhum veículo é tirado de outro usuário e nenhum dado do veículo é alterado.
        </p>
      </div>

      {/* 1. Empresa + cliente */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Empresa</Label>
            <Select
              value={companyId}
              onValueChange={(v) => {
                setCompanyId(v);
                setClientId(undefined);
                reset();
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Escolha a empresa" />
              </SelectTrigger>
              <SelectContent>
                {(companies ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Popover open={clientOpen} onOpenChange={setClientOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" disabled={!companyId || clientsLoading} className="w-full justify-between font-normal">
                  <span className="truncate">
                    {clientsLoading
                      ? "Carregando clientes…"
                      : client
                        ? client.name
                        : companyId
                          ? `Escolha o cliente (${clients?.length ?? 0})`
                          : "Escolha a empresa primeiro"}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Buscar por nome ou e-mail…" />
                  <CommandList>
                    <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                    <CommandGroup>
                      {(clients ?? []).map((c) => (
                        <CommandItem
                          key={c.traccarUserId}
                          value={`${c.name} ${c.email} ${c.traccarUserId}`}
                          onSelect={() => {
                            setClientId(c.traccarUserId);
                            setClientOpen(false);
                            reset();
                          }}
                        >
                          <Check className={cn("h-4 w-4", c.traccarUserId === clientId ? "opacity-100" : "opacity-0")} />
                          <div className="min-w-0">
                            <div className="truncate">{c.name}</div>
                            <div className="truncate text-xs text-muted-foreground">{c.email}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* 2. Placas */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="plates">Placas ou IMEIs</Label>
            <span className="text-xs text-muted-foreground">{plates.length} item(ns)</span>
          </div>
          <Textarea
            id="plates"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              reset();
            }}
            placeholder={"ABC1D23\nXYZ-9876\n869137082197831\n…"}
            className="min-h-[180px] font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Um por linha, ou separados por vírgula (pode colar a coluna direto do Excel). Pode misturar placa e IMEI. Placa
            com ou sem traço; Mercosul e antiga são a mesma (ABC1C34 = ABC1234).
          </p>
        </CardContent>
      </Card>

      {/* 3. Conferir e vincular */}
      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm font-semibold">Conferir e vincular</div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!companyId || !clientId || !plates.length || !!busy} onClick={() => run(true)}>
                {busy === "analyze" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Buscar veículos
              </Button>
              <Button disabled={!results || !counts.link || applied || !!busy} onClick={() => run(false)}>
                {busy === "apply" && <Loader2 className="h-4 w-4 animate-spin" />}
                {applied ? (
                  <>
                    <CheckCircle2 className="h-4 w-4" /> Vinculado
                  </>
                ) : (
                  `Vincular ${counts.link ?? 0} veículo(s)`
                )}
              </Button>
            </div>
          </div>
          {busy && (
            <div className="space-y-1">
              <div className="h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
              </div>
              <div className="text-xs text-muted-foreground">
                {busy === "analyze" ? "Buscando" : "Vinculando"} {progress.done} de {progress.total}…
              </div>
            </div>
          )}
          {!results && !busy && (
            <p className="text-sm text-muted-foreground">
              “Buscar veículos” só mostra o que foi encontrado — nada é vinculado até clicar em “Vincular”.
            </p>
          )}
          {results && (
            <>
              <div className="flex flex-wrap gap-2">
                {statuses.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setFilter((f) => (f === s ? "" : s))}
                    className={cn("rounded-full px-3 py-1 text-xs font-medium", STATUS_LABEL[s].cls, filter === s && "ring-2 ring-primary")}
                  >
                    {STATUS_LABEL[s].label}: {counts[s]}
                  </button>
                ))}
              </div>
              <div className="max-h-[520px] overflow-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-background">
                    <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                      <th className="px-3 py-2">Placa / IMEI</th>
                      <th className="px-3 py-2">Veículo</th>
                      <th className="px-3 py-2">IMEI</th>
                      <th className="px-3 py-2">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((r) => (
                      <tr key={r.plate} className="border-b last:border-0 align-top">
                        <td className="px-3 py-2 font-mono font-medium">{r.plate}</td>
                        <td className="px-3 py-2">
                          {r.deviceName ?? "—"}
                          {r.candidates && (
                            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                              {r.candidates.map((c) => (
                                <li key={c}>{c}</li>
                              ))}
                            </ul>
                          )}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{r.imei ?? "—"}</td>
                        <td className="px-3 py-2">
                          <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", STATUS_LABEL[r.status].cls)}>
                            {r.status === "linked" ? "Vinculado" : STATUS_LABEL[r.status].label}
                          </span>
                          {r.error && r.status !== "not_found" && <div className="mt-1 text-xs text-muted-foreground">{r.error}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {counts.ambiguous ? (
                <p className="text-xs text-muted-foreground">
                  Placas/IMEIs com mais de um veículo não são vinculados — vincule pelo cadastro do usuário escolhendo o veículo certo.
                </p>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
